import React from "react";
import {
 Card,
 H1,
 H3,
 Text,
 Button,
 HTMLSelect,
 Tag,
 Spinner,
 NonIdealState,
 Icon,
} from "@blueprintjs/core";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";
import { DateInput } from "@blueprintjs/datetime";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { inject, observer } from "mobx-react";
import { apiCallPost } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import moment from "moment";
import { getDisplayStatusMeta as getDisplayStatusMetaGlobal } from "../../constants/statusColors";
import { getIsBilledLabel, getIsBilledPill } from "../../utils/isBilledPill";
import { withRouter } from "react-router-dom";

class DischargeRegister extends React.Component {
 state = {
  // Data
  rows: [],
  branches: [],
  doctors: [],

  // Loading states
  isLoading: false,
  isGenerating: false,

  // Filters
  fromDate: new Date(),
  toDate: new Date(),
  selectedBranch: "",
  selectedDoctor: "",
  selectedBranchName: null,
  selectedDoctorName: null,

  // Pagination
  currentPage: 1,
  itemsPerPage: 300,

  // Report data
  allRows: [], // All rows data for client-side filtering

  // AG Grid
  gridApi: null,
  columnApi: null,
 };

 componentDidMount = async () => {
  await this.loadInitialData();
 };

 componentDidUpdate(prevProps) {
  if (
   prevProps.authStore?.selectedBranchName !==
   this.props.authStore?.selectedBranchName
  ) {
   const preferredName = this.props.authStore?.selectedBranchName;
   if (!preferredName) {
    this.setState({
     selectedBranch: "",
     selectedBranchName: null,
    });
    return;
   }
   const norm = (s) =>
    String(s || "")
     .trim()
     .toLowerCase();
   const match = (this.state.branches || []).find(
    (b) => norm(b.name) === norm(preferredName)
   );
   if (match && match._id !== this.state.selectedBranch) {
    this.setState({
     selectedBranch: match._id,
     selectedBranchName: match.name,
    });
   }
  }
 }

 loadInitialData = async () => {
  try {
   this.setState({ isLoading: true });
   const branches = await this.loadBranches();
   await this.loadDoctors();

   console.log("🔍 DEBUG: Branches loaded:", branches.length);
   console.log(
    "🔍 DEBUG: Preferred branch:",
    this.props.authStore?.selectedBranchName
   );

   // Always generate report, even if no specific branch is selected (will fetch all branches)
   if (Array.isArray(branches) && branches.length > 0) {
    const preferredName = this.props.authStore?.selectedBranchName;
    const norm = (s) =>
     String(s || "")
      .trim()
      .toLowerCase();
    const match = preferredName
     ? branches.find((b) => norm(b.name) === norm(preferredName))
     : null;
    const selectedId = match ? match._id : "";

    await this.setState({
     selectedBranch: selectedId,
     selectedBranchName: match?.name || null,
    });
    await this.generateReport();
   } else {
    cogoToast.warn("No branches available. Please contact administrator.");
   }
  } catch (err) {
   console.error("Error loading initial data:", err);
   this.loadDummyData();
  } finally {
   this.setState({ isLoading: false });
  }
 };

 loadBranches = async () => {
  try {
   const response = await apiCallPost(
    `/rai/${this.props.authStore.prefix}/getAllSites`,
    {}
   );
   const raw = response?.data || response || [];
   const branches = (Array.isArray(raw) ? raw : [])
    .map((site, idx) => {
     if (typeof site === "string") {
      return { _id: site, name: site };
     }
     return {
      _id: site?._id || site?.id || site?.siteId || String(idx),
      name:
       site?.site ||
       site?.siteName ||
       site?.name ||
       site?.branchName ||
       site?.label ||
       `Site ${idx + 1}`,
     };
    })
    .filter((b) => b && b._id && b.name)
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
   this.setState({ branches });
   return branches;
  } catch (err) {
   console.error("Error loading branches:", err);
   const fallback = [{ _id: "branch1", name: "Barrackpore" }];
   this.setState({ branches: fallback });
   return fallback;
  }
 };

 loadDoctors = async () => {
  try {
   const response = await apiCallPost(
    `/rai/${this.props.authStore.prefix}/getAllConsultants`,
    {}
   );
   this.setState({ doctors: response || [] });
  } catch (err) {
   console.error("Error loading doctors:", err);
   this.setState({ doctors: [{ _id: "doc1", name: "DR. ARNAB DAS" }] });
  }
 };

 generateReport = async () => {
  try {
   this.setState({ isGenerating: true });

   const payload = {
    page: 1,
    sortBy: "scheduledDate",
    sortOrder: "asc",
    startDate: moment(this.state.fromDate).format("YYYY-MM-DD"),
    endDate: moment(this.state.toDate).format("YYYY-MM-DD"),
    typeOfDate: "scheduledDate",
    status: "Discharged",
   };

   if (this.state.selectedBranch) {
    // Add both id and resolved name to maximize compatibility
    const br = (this.state.branches || []).find(
     (b) => b._id === this.state.selectedBranch
    );
    payload.branchId = this.state.selectedBranch;
    if (br && br.name) payload.branchName = br.name;
   }
   // Note: Doctor filtering is now handled client-side for better UX
   // Only apply server-side filtering for date and branch
   console.log("🔍 DEBUG: API filters applied:", {
    dateRange: `${payload.startDate} to ${payload.endDate}`,
    branchFilter: this.state.selectedBranch || "All branches",
    doctorFilterHandledClientSide: true,
   });

   const response = await apiCallPost(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`,
    payload
   );

   const surgeries = Array.isArray(response)
    ? response
    : Array.isArray(response?.data?.items)
    ? response.data.items
    : Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response?.items)
    ? response.items
    : [];

   console.log("🔍 DEBUG: Discharge Register - API Response:", response);
   console.log(
    "🔍 DEBUG: Discharge Register - Total surgeries:",
    surgeries.length
   );

   // Show sample surgery statuses for debugging
   if (surgeries.length > 0) {
    console.log(
     "🔍 DEBUG: Sample surgery statuses:",
     surgeries.slice(0, 3).map((s) => ({
      status: s.status,
      isBilled: s.isBilled,
      patientName: s.patientName,
     }))
    );
   }

   const filteredDischarged = (surgeries || []).filter((s) => {
    const status = String(s?.status || "")
     .trim()
     .toLowerCase();
    // Only filter by discharged status, don't require isBilled
    return status === "discharged";
   });

   console.log(
    "🔍 DEBUG: Discharge Register - Filtered discharged:",
    filteredDischarged.length
   );

   const rows = this.transformSurgeriesToDischargeRows(filteredDischarged);

   // Store all rows for client-side filtering
   this.setState(
    {
     allRows: rows,
    },
    () => {
     // Apply client-side filters after storing all data
     this.applyClientSideFilters();
    }
   );
  } catch (err) {
   console.error("Error generating Discharge Register:", err);
   cogoToast.error("Failed to generate report. Showing sample data");
   this.loadDummyData();
  } finally {
   this.setState({ isGenerating: false });
  }
 };

 // Normalize booking key events to a map keyed by display header
 buildKeyEventsMap = (booking) => {
  const map = {};
  try {
   const list = Array.isArray(booking?.keyEvents) ? booking.keyEvents : [];
   const byName = {};
   list.forEach((e) => {
    if (!e || !e.event) return;
    const name = String(e.event).trim();
    byName[name] = e.ts ? new Date(e.ts) : null;
   });
   // Map various canonical spellings to headers used in this report
   const read = (k) => byName[k] || null;
   map["Arrival"] = read("Arrival");
   map["Admission"] = read("Admission");
   map["Block Room"] = read("Block Room");
   map["Anaesthesia Start"] = read("Anaesthesia Start");
   map["Surgery Start"] = read("Surgery Start");
   map["Surgery End"] = read("Surgery End");
   map["Anaesthesia End"] = read("Anaesthesia End");
   map["Moved to Ward"] = read("Moved to Ward") || read("Moved To Ward");
   map["Discharge"] = read("Discharge");
  } catch (e) {
   // noop
  }
  return map;
 };

 getDisplayStatusMeta = (row) => {
  const status = row?.status || row?.Status || row?.statusRaw || "-";
  const payorPanel = row?.payorPanel || row?.payor || row?.patientPanel || "";
  const isBilled = row?.isBilled ?? false;
  return getDisplayStatusMetaGlobal({ status, payorPanel, isBilled });
 };

 // Transform surgeries into Discharge Register rows
 transformSurgeriesToDischargeRows = (surgeries) => {
  return (surgeries || []).map((s, idx) => {
   const keyEvents = this.buildKeyEventsMap(s);
   const dischargeTime =
    keyEvents["Discharge"] ||
    (s.dischargedAt ? new Date(s.dischargedAt) : null);
   const statusMeta = this.getDisplayStatusMeta(s);

   // Try to extract OT Number from dedicated field or Admission notes
   let otNumber = s.otNumber || s.otNo || "";
   if (!otNumber && Array.isArray(s.keyEvents)) {
    const adm = s.keyEvents.find((e) => String(e.event).trim() === "Admission");
    if (adm && adm.notes) otNumber = adm.notes;
   }

   return {
    _id: s._id,
    sxNo:
     s.bookingReference ||
     s.docNo ||
     s.surgeryId ||
     (s._id ? String(s._id) : `SX${String(idx + 1).padStart(4, "0")}`),
    patientId:
     s.patientGenericPid || s.patientId || s.patientOriginalData?.sysPid || "",
    patientName: s.patientName || s.patientOriginalData?.name || "",
    doctorName: s.doctorName || "",
    branchName: s.branchName || "",
    otNumber: otNumber || "",
    side: s.eye || s.laterality || "",
    dischargeTime,
    keyEvents,
    status: s.status || "discharged",
    payorPanel: s.payorPanel || s.payor || "",
    isBilled: Boolean(s.isBilled),
    metaStatus: statusMeta.label,
   };
  });
 };

 loadDummyData = () => {
  // Sample rows with key events timeline
  const base = [
   {
    _id: "dummy-id-1",
    sxNo: "I012509120052",
    patientId: "P0101930782",
    patientName: "SAROJ KUMAR SAHA",
    doctorName: "Dr. SOUMEN MONDAL",
    branchName: "Barrackpore",
    otNumber: "OT-1",
    side: "RE",
    dischargeTime: moment().set({ hour: 9, minute: 7 }).toDate(),
    keyEvents: {
     Arrival: moment().set({ hour: 7, minute: 45 }).toDate(),
     Admission: moment().set({ hour: 8, minute: 15 }).toDate(),
     "Block Room": moment().set({ hour: 8, minute: 35 }).toDate(),
     "Anaesthesia Start": moment().set({ hour: 8, minute: 45 }).toDate(),
     "Surgery Start": moment().set({ hour: 9, minute: 0 }).toDate(),
     "Surgery End": moment().set({ hour: 9, minute: 35 }).toDate(),
     "Anaesthesia End": moment().set({ hour: 9, minute: 45 }).toDate(),
     "Moved to Ward": moment().set({ hour: 10, minute: 5 }).toDate(),
     Discharge: moment().set({ hour: 12, minute: 15 }).toDate(),
    },
    status: "discharged",
    payorPanel: "Cash",
    isBilled: true,
   },
   {
    _id: "dummy-id-2",
    sxNo: "I012509120160",
    patientId: "P0101270484",
    patientName: "AKASH VERMA",
    doctorName: "Dr. DINESH RUNGTA",
    branchName: "Barrackpore",
    otNumber: "OT-2",
    side: "LE",
    dischargeTime: moment().set({ hour: 9, minute: 12 }).toDate(),
    keyEvents: {
     Arrival: moment().set({ hour: 7, minute: 55 }).toDate(),
     Admission: moment().set({ hour: 8, minute: 25 }).toDate(),
     "Block Room": moment().set({ hour: 8, minute: 50 }).toDate(),
     "Anaesthesia Start": moment().set({ hour: 9, minute: 0 }).toDate(),
     "Surgery Start": moment().set({ hour: 9, minute: 30 }).toDate(),
     "Surgery End": moment().set({ hour: 10, minute: 10 }).toDate(),
     "Anaesthesia End": moment().set({ hour: 10, minute: 20 }).toDate(),
     "Moved to Ward": moment().set({ hour: 10, minute: 45 }).toDate(),
     Discharge: moment().set({ hour: 12, minute: 30 }).toDate(),
    },
    status: "discharged",
    payorPanel: "Cash",
    isBilled: true,
   },
  ].map((item) => ({
   ...item,
   metaStatus: this.getDisplayStatusMeta(item).label,
  }));

  const rows = base;
  this.setState(
   {
    allRows: rows,
   },
   () => {
    // Apply client-side filters after storing dummy data
    this.applyClientSideFilters();
   }
  );
 };

 handleDateChange = (field, date) => {
  this.setState({ [field]: date });
 };

 handleFilterChange = (field, value) => {
  this.setState({ [field]: value });
 };

 // Helper function to create branch items for dropdown
 createBranchSelectItems = () => {
  return this.state.branches.map((branch) => branch.name).filter(Boolean);
 };

 // Helper function to create doctor items for dropdown with shortcode formatting
 createDoctorSelectItems = () => {
  return this.state.doctors
   .map((doctor) => {
    const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
    const name = doctor.name;
    return `${shortcode}${name}`;
   })
   .filter(Boolean);
 };

 // Helper function to get formatted doctor name
 getFormattedDoctorName = (doctor) => {
  if (!doctor) return null;
  const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
  const name = doctor.name;
  return `${shortcode}${name}`;
 };

 // Helper function to extract doctor name from formatted string
 extractDoctorNameFromFormatted = (formattedName) => {
  if (!formattedName) return null;
  // Remove shortcode part (everything before " | ")
  const withoutShortcode = formattedName.includes(" | ")
   ? formattedName.split(" | ")[1]
   : formattedName;
  return withoutShortcode.trim();
 };

 // Apply client-side filtering to existing data
 applyClientSideFilters = () => {
  let filteredRows = [...this.state.allRows];

  // Apply doctor filter if selected
  if (this.state.selectedDoctor && this.state.selectedDoctorName) {
   const extractedDoctorName = this.extractDoctorNameFromFormatted(
    this.state.selectedDoctorName
   );
   console.log(
    "🔍 DEBUG: Applying client-side doctor filter:",
    extractedDoctorName
   );

   filteredRows = filteredRows.filter((row) => {
    const rowDoctorName = row.doctorName || "";
    // Try multiple matching strategies
    const matches = [
     // Exact match
     rowDoctorName === extractedDoctorName,
     // Match without "DR. " prefix
     rowDoctorName.replace(/^DR\.\s*/i, "") ===
      extractedDoctorName.replace(/^DR\.\s*/i, ""),
     // Case-insensitive match
     rowDoctorName.toLowerCase() === extractedDoctorName.toLowerCase(),
     // Partial match (contains)
     rowDoctorName.toLowerCase().includes(extractedDoctorName.toLowerCase()),
    ].some(Boolean);

    return matches;
   });

   console.log("🔍 DEBUG: Client-side filtering result:", {
    original: this.state.allRows.length,
    filtered: filteredRows.length,
    extractedDoctorName,
   });
  }

  // Update state with filtered data
  this.setState({ rows: filteredRows });

  // Show feedback to user
  const filterMsg = this.state.selectedDoctorName
   ? `Filtered to ${
      filteredRows.length
     } records for ${this.extractDoctorNameFromFormatted(
      this.state.selectedDoctorName
     )}`
   : `Showing all ${filteredRows.length} records`;

  cogoToast.success(filterMsg);
 };

 // Handle branch selection from SingleSelectWrapper
 handleBranchFilterSelect = (branchName) => {
  const selectedBranch = this.state.branches.find(
   (branch) => branch.name === branchName
  );
  this.setState({
   selectedBranch: selectedBranch ? selectedBranch._id : "",
   selectedBranchName: branchName || null,
  });
 };

 // Handle doctor selection from SingleSelectWrapper
 handleDoctorFilterSelect = (formattedDoctorName) => {
  // Find the doctor by matching the formatted name
  const selectedDoctor = this.state.doctors.find((doctor) => {
   const formatted = this.getFormattedDoctorName(doctor);
   return formatted === formattedDoctorName;
  });

  this.setState(
   {
    selectedDoctor: selectedDoctor ? selectedDoctor._id : "",
    selectedDoctorName: formattedDoctorName || null,
   },
   () => {
    // Apply filtering immediately if we have data
    if (this.state.allRows.length > 0) {
     this.applyClientSideFilters();
    }
   }
  );
 };

 getTotalPages = () => {
  return (
   Math.ceil((this.state.rows || []).length / this.state.itemsPerPage) || 1
  );
 };

 getPaginatedRows = () => {
  const all = this.state.rows || [];
  const { currentPage, itemsPerPage } = this.state;
  const startIndex = (currentPage - 1) * itemsPerPage;
  return all.slice(startIndex, startIndex + itemsPerPage);
 };

 handlePageChange = (page) => {
  const totalPages = this.getTotalPages();
  const newPage = Math.min(Math.max(1, page), totalPages);
  this.setState({ currentPage: newPage });
 };

 timeOrDash = (d) => (d ? moment(d).format("DD-MM-YYYY, HH:mm") : "-");

 // AG Grid configuration
 onGridReady = (params) => {
  this.setState({
   gridApi: params.api,
   columnApi: params.columnApi,
  });
 };

 // Get only visible/displayed columns (respects sidebar column selection)
 getDisplayedColumns = () => {
  if (!this.state.columnApi) return this.getColumnDefs();
  const allColumns = this.state.columnApi.getAllColumns();
  if (!allColumns) return this.getColumnDefs();
  const visibleColumns = allColumns.filter((col) => col.isVisible());
  return visibleColumns.map((col) => {
   const colDef = col.getColDef();
   return {
    headerName: colDef.headerName,
    field: colDef.field,
    ...colDef,
   };
  });
 };

 // Column definitions for AG Grid
 getColumnDefs = () => {
  const keyEventHeaders = [
   "Arrival",
   "Admission",
   "Block Room",
   "Anaesthesia Start",
   "Surgery Start",
   "Surgery End",
   "Anaesthesia End",
   "Moved to Ward",
   "Discharge",
  ];

  const columns = [
   {
    headerName: "Sl.",
    valueGetter: (params) => {
     return params.node.rowIndex + 1;
    },
    width: 60,
    pinned: "left",
    filter: false,
    sortable: false,
   },
   {
    headerName: "IPD No.",
    field: "sxNo",
    width: 140,
    pinned: "left",
    filter: "agTextColumnFilter",
    cellRenderer: (params) => {
     if (!params.value) return "-";
     const id = params.data._id || params.value;
     return `<a href="/v3/workflow/surgery-booking/${encodeURIComponent(id)}" 
                     target="_blank" 
                     rel="noopener noreferrer" 
                     style="color: var(--intent-primary); text-decoration: underline;">
                     ${params.value}
                   </a>`;
    },
   },
   {
    headerName: "Patient ID",
    field: "patientId",
    width: 120,
    filter: "agTextColumnFilter",
    cellRenderer: (params) => params.value || "-",
   },
   {
    headerName: "Patient Name",
    field: "patientName",
    width: 180,
    filter: "agTextColumnFilter",
    cellRenderer: (params) => params.value || "-",
   },
   {
    headerName: "Surgeon",
    field: "doctorName",
    width: 150,
    filter: "agTextColumnFilter",
    cellRenderer: (params) => {
     const name = params.value || "";
     return name ? name.replace("Dr. ", "") : "-";
    },
   },
   {
    headerName: "Status",
    field: "metaStatus",
    width: 160,
    filter: "agTextColumnFilter",
    cellRenderer: (params) => params.value || "-",
   },
   {
    headerName: "Is Billed",
    field: "isBilled",
    width: 120,
    filter: "agSetColumnFilter",
    valueGetter: (params) => getIsBilledLabel(params.data),
    valueFormatter: (params) => getIsBilledLabel(params.data),
    cellRenderer: (params) => getIsBilledPill(params.data),
    cellStyle: { textAlign: "center" },
   },
  ];

  // Add key event columns
  keyEventHeaders.forEach((header) => {
   columns.push({
    headerName: header,
    field: `keyEvents.${header}`,
    width: 140,
    filter: "agDateColumnFilter",
    valueGetter: (params) => {
     const keyEvents = params.data.keyEvents || {};
     const date = keyEvents[header];
     return date ? moment(date).format("DD-MM-YYYY, HH:mm") : "-";
    },
    comparator: (valueA, valueB) => {
     const parseDate = (val) => {
      if (val === "-") return null;
      return moment(val, "DD-MM-YYYY, HH:mm").toDate();
     };
     const dateA = parseDate(valueA);
     const dateB = parseDate(valueB);
     if (!dateA && !dateB) return 0;
     if (!dateA) return 1;
     if (!dateB) return -1;
     return dateA.getTime() - dateB.getTime();
    },
   });
  });

  // Add remaining columns
  columns.push(
   {
    headerName: "OT Number",
    field: "otNumber",
    width: 100,
    filter: "agTextColumnFilter",
    cellRenderer: (params) => params.value || "-",
   },
   {
    headerName: "Branch",
    field: "branchName",
    width: 120,
    filter: "agTextColumnFilter",
    cellRenderer: (params) => params.value || "-",
   }
  );

  return columns;
 };

 renderFilters = () => {
  return (
   <Card
    elevation={2}
    style={{
     padding: "20px",
     backgroundColor: "var(--bg-surface)",
     border: "1px solid var(--border-color)",
     marginBottom: "20px",
    }}
   >
    <div
     style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}
    >
     <Icon
      icon="filter"
      style={{ marginRight: "10px", color: "var(--text-primary)" }}
     />
     <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Filters</H3>
     <div
      style={{
       marginLeft: "auto",
       display: "flex",
       alignItems: "center",
       gap: "10px",
      }}
     >
      <Tag intent="success" large>
       {this.state.rows.length} Records
      </Tag>
     </div>
    </div>

    <div
     style={{
      display: "flex",
      alignItems: "center",
      gap: "15px",
      flexWrap: "nowrap",
     }}
    >
     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       From
      </Tag>
      <DateInput
       value={this.state.fromDate}
       onChange={(d) => this.handleDateChange("fromDate", d)}
       formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
       parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
       placeholder="From Date"
       style={{ width: "140px" }}
       rightElement={<Icon icon="calendar" size={14} />}
      />
     </div>

     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       To
      </Tag>
      <DateInput
       value={this.state.toDate}
       onChange={(d) => this.handleDateChange("toDate", d)}
       formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
       parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
       placeholder="To Date"
       style={{ width: "140px" }}
       rightElement={<Icon icon="calendar" size={14} />}
      />
     </div>

     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       Branch
      </Tag>
      <div style={{ width: "160px" }}>
       <SingleSelectWrapper
        items={[
         `All Branches (${this.state.branches.length})`,
         ...this.createBranchSelectItems(),
        ]}
        selectedItem={
         this.state.selectedBranchName ||
         `All Branches (${this.state.branches.length})`
        }
        placeholder="Search branches..."
        onChange={(value) => {
         if (value === `All Branches (${this.state.branches.length})`) {
          this.handleBranchFilterSelect(null);
         } else {
          this.handleBranchFilterSelect(value);
         }
        }}
        width="100%"
       />
      </div>
     </div>

     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
       Doctor
      </Tag>
      <div style={{ width: "180px" }}>
       <SingleSelectWrapper
        items={[
         `All Doctors (${this.state.doctors.length})`,
         ...this.createDoctorSelectItems(),
        ]}
        selectedItem={
         this.state.selectedDoctorName ||
         `All Doctors (${this.state.doctors.length})`
        }
        placeholder="Search doctors..."
        onChange={(value) => {
         if (value === `All Doctors (${this.state.doctors.length})`) {
          this.handleDoctorFilterSelect(null);
         } else {
          this.handleDoctorFilterSelect(value);
         }
        }}
        width="100%"
       />
      </div>
     </div>

     <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
      <Button
       intent="primary"
       icon="search"
       onClick={this.generateReport}
       loading={this.state.isGenerating}
      >
       Generate
      </Button>
      <Button
       icon="document"
       onClick={this.exportToPDF}
       disabled={!this.state.rows.length}
      >
       PDF
      </Button>
      <Button
       icon="th"
       onClick={this.exportToCSV}
       disabled={!this.state.rows.length}
      >
       CSV
      </Button>
     </div>
    </div>
   </Card>
  );
 };

 renderTable = () => {
  if (this.state.isGenerating) {
   return (
    <NonIdealState
     icon={<Spinner />}
     title="Generating Report..."
     description="Please wait while we fetch the discharge data"
    />
   );
  }

  if (!this.state.rows.length) {
   return (
    <NonIdealState
     icon="th"
     title="No Records"
     description="No data found for selected filters"
    />
   );
  }

  return (
   <div
    className="ag-theme-alpine-dark"
    style={{
     height: "600px",
     width: "100%",
     border: "1px solid var(--border-color)",
     borderRadius: "8px",
    }}
   >
    <AgGridReact
     columnDefs={this.getColumnDefs()}
     rowData={this.state.rows}
     onGridReady={this.onGridReady}
     defaultColDef={{
      sortable: true,
      resizable: true,
      filter: true,
      menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
     }}
     sideBar={{
      toolPanels: [
       {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        toolPanelParams: {
         suppressRowGroups: true,
         suppressValues: true,
         suppressPivots: true,
         suppressPivotMode: true,
         suppressColumnFilter: false,
         suppressColumnSelectAll: false,
         suppressColumnExpandAll: false,
        },
       },
       {
        id: "filters",
        labelDefault: "Filters",
        labelKey: "filters",
        iconKey: "filter",
        toolPanel: "agFiltersToolPanel",
        toolPanelParams: {
         suppressExpandAll: false,
         suppressFilterSearch: false,
        },
       },
      ],
      defaultToolPanel: null, // No panel open by default
      hiddenByDefault: false,
      position: "right",
     }}
     animateRows={true}
     enableRangeSelection={true}
     enableCellTextSelection={true}
     suppressRowClickSelection={false}
     rowSelection="multiple"
     pagination={true}
     paginationPageSize={100}
     paginationAutoPageSize={false}
     suppressPaginationPanel={false}
     domLayout="normal"
    />
   </div>
  );
 };

 renderPagination = () => {
  const totalPages = this.getTotalPages();
  const currentPage = this.state.currentPage;
  const totalItems = (this.state.rows || []).length;
  const startItem = (currentPage - 1) * this.state.itemsPerPage + 1;
  const endItem = Math.min(currentPage * this.state.itemsPerPage, totalItems);

  if (totalPages <= 1) return null;

  return (
   <div
    style={{
     display: "flex",
     justifyContent: "space-between",
     alignItems: "center",
     padding: "15px",
     backgroundColor: "var(--bg-secondary)",
     border: "1px solid var(--border-color)",
     borderRadius: "8px",
     marginTop: "20px",
    }}
   >
    <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
     Showing {startItem}-{endItem} of {totalItems} records
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
     <Button
      icon="chevron-left"
      onClick={() => this.handlePageChange(currentPage - 1)}
      disabled={currentPage === 1}
      minimal
     />
     <div style={{ display: "flex", gap: "4px" }}>
      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
       let pageNum;
       if (totalPages <= 5) pageNum = i + 1;
       else if (currentPage <= 3) pageNum = i + 1;
       else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
       else pageNum = currentPage - 2 + i;
       return (
        <Button
         key={pageNum}
         onClick={() => this.handlePageChange(pageNum)}
         intent={currentPage === pageNum ? "primary" : "none"}
         minimal
         style={{
          minWidth: "32px",
          backgroundColor:
           currentPage === pageNum ? "var(--intent-primary)" : "transparent",
         }}
        >
         {pageNum}
        </Button>
       );
      })}
     </div>
     <Button
      icon="chevron-right"
      onClick={() => this.handlePageChange(currentPage + 1)}
      disabled={currentPage === totalPages}
      minimal
     />
    </div>
   </div>
  );
 };

 renderHeader = () => {
  const branchName =
   this.state.branches.find((b) => b._id === this.state.selectedBranch)?.name ||
   "All Branches";
  const doctorName = this.state.selectedDoctorName
   ? this.extractDoctorNameFromFormatted(this.state.selectedDoctorName)
   : "All Doctors";
  return (
   <div
    style={{
     textAlign: "center",
     marginBottom: "20px",
     borderBottom: "2px solid var(--border-color)",
     paddingBottom: "12px",
    }}
   >
    <H1 style={{ color: "var(--text-primary)", margin: 0, fontSize: "24px" }}>
     Patient Discharge List - {this.props.authStore?.hospitalName || "DISHA EYE HOSPITALS PRIVATE LIMITED"}
    </H1>
    <Text
     style={{
      color: "var(--text-secondary)",
      margin: "5px 0",
      fontSize: "16px",
     }}
    >
     {branchName} {doctorName !== "All Doctors" ? `• ${doctorName}` : ""} -{" "}
     {moment(this.state.fromDate).format("DD-MMM-YYYY")} to{" "}
     {moment(this.state.toDate).format("DD-MMM-YYYY")}
    </Text>
   </div>
  );
 };

 exportToCSV = () => {
  // Get filtered data from AG Grid
  let filteredData = [];
  if (this.state.gridApi) {
   this.state.gridApi.forEachNodeAfterFilter((node) => {
    filteredData.push(node.data);
   });
  } else {
   filteredData = this.state.rows;
  }

  // Get only visible columns (respects sidebar column selection)
  const columns = this.getDisplayedColumns();

  // Build CSV headers
  const headers = columns.map((col) => col.headerName);

  // Build CSV data rows
  const rows = (filteredData || []).map((rowData, idx) => {
   return columns.map((col) => {
    // Handle special columns
    if (col.headerName === "Sl.") {
     return `${idx + 1}`;
    }

    // Handle key event columns
    if (col.field && col.field.startsWith("keyEvents.")) {
     const eventName = col.field.replace("keyEvents.", "");
     return this.timeOrDash(rowData.keyEvents?.[eventName]);
    }

    // Handle doctor name formatting
    if (col.field === "doctorName") {
     return rowData.doctorName ? rowData.doctorName.replace("Dr. ", "") : "-";
    }

    // Default field value
    return rowData[col.field] || "-";
   });
  });

  // Create CSV content
  const csvRows = [headers.join(",")];
  rows.forEach((r) =>
   csvRows.push(
    r.map((c) => `"${(`${c}` || "").replace(/"/g, '""')}`).join(",")
   )
  );
  const blob = new Blob([csvRows.join("\n")], {
   type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `discharge-register-${moment().format("YYYYMMDD-HHmm")}.csv`;
  link.click();
  URL.revokeObjectURL(url);
  cogoToast.success("CSV exported (filtered data with selected columns)");
 };

 exportToPDF = () => {
  // Get filtered data from AG Grid
  let filteredData = [];
  if (this.state.gridApi) {
   // Get all filtered rows from AG Grid
   this.state.gridApi.forEachNodeAfterFilter((node) => {
    filteredData.push(node.data);
   });
  } else {
   // Fallback to state data if grid API not available
   filteredData = this.state.rows;
  }

  // Get only visible columns (respects sidebar column selection)
  const columns = this.getDisplayedColumns();

  const branchName =
   this.state.branches.find((b) => b._id === this.state.selectedBranch)?.name ||
   "All Branches";
  const doctorName = this.state.selectedDoctorName
   ? this.extractDoctorNameFromFormatted(this.state.selectedDoctorName)
   : "All Doctors";

  const win = window.open("", "_blank");
  if (!win) {
   cogoToast.error("Popup blocked. Allow popups to export PDF");
   return;
  }

  const styles = `
      <style>
        body { font-family: Arial, sans-serif; padding: 16px; }
        h2 { margin: 0 0 8px 0; }
        .meta { color: #555; margin-bottom: 12px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #999; padding: 6px; text-align: left; }
        th { background: #eee; }
        .pill { display: inline-block; padding: 3px 10px; border-radius: 10px; background: #106ba3; color: #fff; font-weight: 700; font-size: 13px; border: 1px solid #0d5b8a; }
        @media print { * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } th { background: #e6e6e6 !important; } .pill { background: #106ba3 !important; color: #fff !important; border-color: #0d5b8a !important; } }
      </style>
    `;

  const headerHtml = `
      <h2>Patient Discharge List</h2>
      <div class="meta">${branchName}${
   doctorName !== "All Doctors" ? ` • ${doctorName}` : ""
  } - ${moment(this.state.fromDate).format("DD-MMM-YYYY")} to ${moment(
   this.state.toDate
  ).format("DD-MMM-YYYY")} | ${filteredData.length} records</div>
    `;

  const thead = `<tr>${columns
   .map((col) => `<th>${col.headerName}</th>`)
   .join("")}</tr>`;
  const tbody = (filteredData || [])
   .map((rowData, idx) => {
    const cells = columns
     .map((col) => {
      // Handle special columns
      if (col.headerName === "Sl.") {
       return `<td>${idx + 1}</td>`;
      }

      // Handle key event columns
      if (col.field && col.field.startsWith("keyEvents.")) {
       const eventName = col.field.replace("keyEvents.", "");
       return `<td>${this.timeOrDash(rowData.keyEvents?.[eventName])}</td>`;
      }

      // Handle doctor name formatting
      if (col.field === "doctorName") {
       return `<td>${
        rowData.doctorName ? rowData.doctorName.replace("Dr. ", "") : "-"
       }</td>`;
      }

      // Default field value
      return `<td>${rowData[col.field] || "-"}</td>`;
     })
     .join("");
    return `<tr>${cells}</tr>`;
   })
   .join("");

  win.document.write(
   `<!doctype html><html><head><meta charset="utf-8"/>${styles}</head><body>${headerHtml}<table>${thead}${tbody}</table></body></html>`
  );
  win.document.close();
  win.focus();
  win.print();
  cogoToast.success("PDF opened for printing (with selected columns)");
 };

 render() {
  if (this.state.isLoading) {
   return (
    <NonIdealState
     icon={<Spinner />}
     title="Loading..."
     description="Preparing filters and data"
    />
   );
  }
  return (
   <div
    style={{
     padding: "20px",
     backgroundColor: "var(--bg-primary)",
     color: "var(--text-primary)",
     minHeight: "100vh",
    }}
   >
    <div style={{ marginBottom: "20px" }}>
     <H1 style={{ color: "var(--text-primary)", margin: 0 }}>
      Discharge Register
     </H1>
     <Text
      style={{
       color: "var(--text-secondary)",
       margin: "5px 0",
       fontSize: "16px",
      }}
     >
      Filter by date, branch and doctor. IPD No. shows Surgery Number. Discharge
      Time is the first time column; key events follow.
     </Text>
    </div>

    {this.renderFilters()}

    <Card
     elevation={2}
     style={{
      padding: "20px",
      backgroundColor: "var(--bg-surface)",
      border: "1px solid var(--border-color)",
     }}
    >
     {this.renderHeader()}
     {this.renderTable()}
    </Card>
   </div>
  );
 }
}

export default withRouter(inject("authStore")(observer(DischargeRegister)));
